import { useCallback, useState } from 'react'

// A thin useState-shaped wrapper around localStorage.
//
// Callers are responsible for validating the returned value — we give back
// whatever JSON.parse produced, typed as T. If the stored shape might have
// drifted (old app version, hand-edited devtools), parse it with Zod at the
// call site before trusting it.
export const useLocalStorage = <T>(
	key: string,
	initial?: T,
): [T | undefined, (next: T | undefined) => void] => {
	// Read storage once, when this hook first mounts. useState's initializer
	// form takes a function so the read only runs on the first render, not
	// on every re-render.
	const [value, setValue] = useState<T | undefined>(() => {
		// localStorage.getItem can throw in Safari private mode or if storage
		// is disabled — fall back to the supplied initial in that case.
		let raw: string | null
		try {
			raw = window.localStorage.getItem(key)
		} catch {
			return initial
		}

		// Nothing stored yet — use the initial value (which may itself be
		// undefined if the caller didn't supply one).
		if (raw === null) return initial

		// JSON.parse throws on malformed input. Defensive: we wrote this
		// string, but another tab/devtools/old version might have corrupted
		// it.
		try {
			return JSON.parse(raw) as T
		} catch {
			return initial
		}
	})

	// Combined setter: updates the React state AND writes through to
	// localStorage in one step. Keeping them together means the two can
	// never disagree.
	const update = useCallback(
		(next: T | undefined) => {
			setValue(next)

			// Passing `undefined` clears the entry entirely, which matches
			// the type signature and lets callers "forget" a value.
			if (next === undefined) {
				try {
					window.localStorage.removeItem(key)
				} catch {
					// storage disabled — nothing to do
				}
				return
			}

			try {
				window.localStorage.setItem(key, JSON.stringify(next))
			} catch {
				// storage full or disabled — keep the in-memory value and
				// move on; persistence is best-effort.
			}
		},
		[key],
	)

	return [value, update]
}
